import { useRef, useState } from 'react';
import { ApiError, api, mediaUrl } from '../lib/api';
import { compressImage } from '../lib/compressImage';
import { haptic, notify } from '../lib/telegram';
import type { PostMediaItem } from '../types';
import { ErrorBanner } from './Ui';

type Props = {
  media: PostMediaItem[];
  onChange: (media: PostMediaItem[]) => void;
  /** Создаёт черновик, если его ещё нет, и отдаёт его id. */
  ensureCollectionId: () => Promise<string>;
  limits: { maxMedia: number; maxPhotoMb: number; maxVideoMb: number };
  /** Поставить файл в текст поста — тогда он окажется там, а не в шапке. */
  onInsertIntoText?: (item: PostMediaItem, position: number) => void;
};

export function MediaUploader({
  media,
  onChange,
  ensureCollectionId,
  limits,
  onInsertIntoText,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<{ name: string; percent: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const free = limits.maxMedia - media.length;

  const pickFiles = () => inputRef.current?.click();

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError(null);

    let collectionId: string;
    try {
      collectionId = await ensureCollectionId();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить черновик');
      return;
    }

    const queue = Array.from(files).slice(0, Math.max(0, free));
    if (queue.length < files.length) {
      setError(`За раз можно добавить ещё ${Math.max(0, free)} — предел ${limits.maxMedia} файлов`);
    }

    let current = media;
    for (const file of queue) {
      try {
        setUploading({ name: file.name, percent: 0 });
        // Фото ужимаем на устройстве: оригинал со смартфона весит мегабайты,
        // а Telegram всё равно покажет его не крупнее ~1280px.
        const { file: payload } = await compressImage(file);
        const uploaded = await api.uploadMedia(collectionId, payload, (percent) =>
          setUploading({ name: file.name, percent }),
        );
        current = [...current, uploaded];
        onChange(current);
        haptic('light');
      } catch (err) {
        notify('error');
        setError(err instanceof ApiError ? `${file.name}: ${err.message}` : `${file.name}: ошибка загрузки`);
        break;
      } finally {
        setUploading(null);
      }
    }

    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = async (item: PostMediaItem) => {
    const collectionId = await ensureCollectionId();
    try {
      const next = await api.deleteMedia(collectionId, item.id);
      onChange(next);
      haptic('light');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось удалить файл');
    }
  };

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= media.length) return;
    const ids = media.map((m) => m.id);
    const [moved] = ids.splice(index, 1);
    ids.splice(target, 0, moved!);

    // Показываем новый порядок сразу, сервер подтверждает следом.
    const optimistic = ids.map((id) => media.find((m) => m.id === id)!);
    onChange(optimistic);

    try {
      const collectionId = await ensureCollectionId();
      onChange(await api.reorderMedia(collectionId, ids));
    } catch (err) {
      onChange(media);
      setError(err instanceof ApiError ? err.message : 'Не удалось изменить порядок');
    }
  };

  return (
    <div className="uploader">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm"
        multiple
        hidden
        onChange={(e) => void handleFiles(e.target.files)}
      />

      {media.length > 0 && (
        <div className="uploader__grid">
          {media.map((item, index) => (
            <figure className="uploader__item" key={item.id}>
              {item.kind === 'video' ? (
                <video
                  className="uploader__thumb"
                  src={mediaUrl(item.url)}
                  muted
                  playsInline
                  // Без этого браузер сам решает, сколько скачать вперёд, и на
                  // ролике в полсотни мегабайт редактор открывается долго. Нужен только
                  // первый кадр, а сервер умеет отдавать кусок по Range.
                  preload="metadata"
                />
              ) : (
                <img className="uploader__thumb" src={mediaUrl(item.url)} alt={item.originalName} />
              )}

              {item.kind === 'video' && <span className="uploader__badge">видео</span>}

              <figcaption className="uploader__controls">
                <button
                  className="icon-btn"
                  onClick={() => void move(index, -1)}
                  disabled={index === 0}
                  aria-label="Левее"
                >
                  ←
                </button>
                <button
                  className="icon-btn"
                  onClick={() => void move(index, 1)}
                  disabled={index === media.length - 1}
                  aria-label="Правее"
                >
                  →
                </button>
                {onInsertIntoText && (
                  <button
                    className="icon-btn"
                    onClick={() => onInsertIntoText(item, index)}
                    aria-label="Поставить в текст"
                    title="Поставить в текст поста"
                  >
                    ¶
                  </button>
                )}
                <button
                  className="icon-btn icon-btn--danger"
                  onClick={() => void remove(item)}
                  aria-label="Удалить"
                >
                  ✕
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {uploading && (
        <div className="uploader__progress">
          <div className="uploader__progress-bar" style={{ width: `${uploading.percent}%` }} />
          <span className="uploader__progress-label">
            {uploading.name} · {uploading.percent}%
          </span>
        </div>
      )}

      <button className="uploader__add" onClick={pickFiles} disabled={free <= 0 || Boolean(uploading)}>
        {free > 0 ? '＋ Добавить фото или видео' : `Достигнут предел ${limits.maxMedia} файлов`}
      </button>

      <p className="muted small">
        До {limits.maxMedia} файлов в посте. Фото сжимаются на устройстве, видео до{' '}
        {limits.maxVideoMb} МБ. Порядок в шапке поста — слева направо.
        {onInsertIntoText && ' Кнопка ¶ ставит файл внутрь текста, между абзацами.'}
      </p>

      {error && <ErrorBanner>{error}</ErrorBanner>}
    </div>
  );
}
